/**
 * lucide-react → Phosphor Duotone shim.
 *
 * Vite aliases `lucide-react` to this file so every existing
 * `import { Foo } from "lucide-react"` call site renders a
 * premium Phosphor duotone glyph instead of the thin Lucide line icon.
 *
 * - Default weight is "duotone" (override with weight prop).
 * - Lucide's `strokeWidth` prop is silently ignored (Phosphor uses `weight`).
 * - Loader2 auto-spins to preserve existing behavior.
 */
import * as React from "react";
import * as Ph from "@phosphor-icons/react";
import type { Icon as PhIcon, IconProps as PhIconProps } from "@phosphor-icons/react";

export type LucideIcon = React.ForwardRefExoticComponent<
  Omit<PhIconProps, "weight"> & { weight?: PhIconProps["weight"]; strokeWidth?: number | string }
>;
export type LucideProps = PhIconProps & { strokeWidth?: number | string };

const PhAny = Ph as unknown as Record<string, PhIcon | undefined>;

const make = (Icon: PhIcon, opts: { spin?: boolean } = {}): LucideIcon =>
  React.forwardRef<SVGSVGElement, any>(({ strokeWidth: _sw, weight, className, ...rest }, ref) => {
    const cls = opts.spin ? `animate-spin ${className ?? ""}`.trim() : className;
    return React.createElement(Icon, {
      ref,
      weight: weight ?? "duotone",
      className: cls,
      ...rest,
    });
  }) as unknown as LucideIcon;

// Fallback for any name we forgot to map
const Fallback = make((PhAny.CircleIcon ?? PhAny.Circle)!);

const m = (name: string): LucideIcon => {
  const I = PhAny[`${name}Icon`] ?? PhAny[name];
  return I ? make(I) : Fallback;
};

// ───────── Activity / status
export const Activity = m("Activity");
export const ActivityIcon = Activity;
export const HeartPulse = m("Heartbeat");
export const Pulse = Activity;

// ───────── Alerts
export const AlertCircle = m("WarningCircle");
export const AlertTriangle = m("Warning");
export const Ban = m("Prohibit");
export const Bug = m("Bug");
export const Info = m("Info");
export const HelpCircle = m("Question");

// ───────── Arrows / chevrons
export const ArrowDown = m("ArrowDown");
export const ArrowDownCircle = m("ArrowCircleDown");
export const ArrowDownRight = m("ArrowDownRight");
export const ArrowLeft = m("ArrowLeft");
export const ArrowRight = m("ArrowRight");
export const ArrowRightLeft = m("ArrowsLeftRight");
export const ArrowUp = m("ArrowUp");
export const ArrowUpDown = m("ArrowsDownUp");
export const ArrowUpRight = m("ArrowUpRight");
export const ChevronDown = m("CaretDown");
export const ChevronLeft = m("CaretLeft");
export const ChevronRight = m("CaretRight");
export const ChevronUp = m("CaretUp");
export const Undo2 = m("ArrowUUpLeft");
export const RefreshCw = m("ArrowsClockwise");
export const RefreshCcw = m("ArrowCounterClockwise");
export const Repeat = m("Repeat");

// ───────── Charts / analytics
export const BarChart = m("ChartBar");
export const BarChart3 = m("ChartBar");
export const LineChart = m("ChartLine");
export const TrendingDown = m("TrendDown");
export const TrendingUp = m("TrendUp");
export const TrendingUpIcon = TrendingUp;

// ───────── Bell / notifications
export const Bell = m("Bell");
export const BellRing = m("BellRinging");

// ───────── Books / docs
export const BookOpen = m("BookOpen");
export const Bookmark = m("Bookmark");
export const Quote = m("Quotes");

// ───────── People / business
export const Brain = m("Brain");
export const Briefcase = m("Briefcase");
export const Building = m("Buildings");
export const Building2 = m("Buildings");
export const GraduationCap = m("GraduationCap");
export const Crown = m("Crown");

// ───────── Calendar / time
export const Calendar = m("Calendar");
export const CalendarCheck = m("CalendarCheck");
export const CalendarClock = m("Calendar");
export const CalendarIcon = Calendar;
export const CalendarRange = m("Calendar");
export const Clock = m("Clock");
export const Timer = m("Timer");
export const History = m("ClockCounterClockwise");

// ───────── Calculator / money
export const Calculator = m("Calculator");
export const CreditCard = m("CreditCard");
export const DollarSign = m("CurrencyDollar");
export const PoundSterling = m("CurrencyGbp");
export const Percent = m("Percent");
export const Scale = m("Scales");
export const Receipt = m("Receipt");

// ───────── Check / state
export const Check = m("Check");
export const CheckCheck = m("Checks");
export const CheckCircle = m("CheckCircle");
export const CheckCircle2 = m("CheckCircle");
export const CheckSquare = m("CheckSquare");
export const Circle = m("Circle");
export const Square = m("Square");
export const Dot = m("Dot");

// ───────── Code / dev
export const Code = m("Code");
export const Code2 = m("Code");
export const Cookie = m("Cookie");
export const Database = m("Database");
export const Server = m("HardDrives");
export const Network = m("ShareNetwork");
export const TestTube2 = m("TestTube");
export const Webhook = m("Plugs");
export const WebhookIcon = Webhook;

// ───────── File / media
export const Copy = m("Copy");
export const Download = m("DownloadSimple");
export const FileDown = m("FileArrowDown");
export const File = m("File");
export const FileJson = m("FileCode");
export const FileSpreadsheet = m("FileXls");
export const FileText = m("FileText");
export const FileX = m("FileX");
export const FolderOpen = m("FolderOpen");
export const Image = m("Image");
export const ImageIcon = Image;
export const Paperclip = m("Paperclip");
export const Printer = m("Printer");
export const Save = m("FloppyDisk");
export const Upload = m("UploadSimple");

// ───────── Filter / search
export const Filter = m("Funnel");
export const Search = m("MagnifyingGlass");
export const SlidersHorizontal = m("SlidersHorizontal");
export const ZoomIn = m("MagnifyingGlassPlus");

// ───────── Edit
export const Edit = m("PencilSimple");
export const Edit2 = m("PencilSimple");
export const Edit3 = m("PencilSimpleLine");
export const Pencil = m("Pencil");
export const Trash = m("Trash");
export const Trash2 = m("Trash");
export const Wand2 = m("MagicWand");

// ───────── Eye / visibility
export const Eye = m("Eye");
export const EyeOff = m("EyeSlash");

// ───────── Layout
export const Grid3X3 = m("GridNine");
export const GripVertical = m("DotsSixVertical");
export const Layers = m("Stack");
export const LayoutDashboard = m("SquaresFour");
export const LayoutGrid = m("GridFour");
export const List = m("List");
export const ListChecks = m("ListChecks");
export const ListTodo = m("ListChecks");
export const Maximize2 = m("ArrowsOut");
export const Menu = m("List");
export const Minimize2 = m("ArrowsIn");
export const MoreHorizontal = m("DotsThree");
export const MoreVertical = m("DotsThreeVertical");
export const PanelLeft = m("SidebarSimple");

// ───────── Loader / loaders
export const Loader2 = make((PhAny.CircleNotchIcon ?? PhAny.CircleNotch)!, { spin: true });

// ───────── Comms
export const Headphones = m("Headphones");
export const Mail = m("Envelope");
export const MailCheck = m("EnvelopeOpen");
export const Megaphone = m("Megaphone");
export const MessageCircle = m("ChatCircle");
export const MessageCircleQuestion = m("ChatCircleDots");
export const MessageSquare = m("Chat");
export const MessageSquarePlus = m("ChatTeardropDots");
export const MessageSquareWarning = m("ChatCenteredText");
export const MessagesSquare = m("ChatsCircle");
export const Send = m("PaperPlaneTilt");
export const Phone = m("Phone");

// ───────── Emotions / feedback
export const Frown = m("SmileySad");
export const Heart = m("Heart");
export const HeartCrack = m("HeartBreak");
export const Meh = m("SmileyMeh");
export const Smile = m("Smiley");
export const Star = m("Star");
export const ThumbsDown = m("ThumbsDown");
export const ThumbsUp = m("ThumbsUp");
export const Trophy = m("Trophy");
export const Target = m("Target");
export const Flag = m("Flag");
export const Flame = m("Flame");
export const Gift = m("Gift");
export const Lightbulb = m("Lightbulb");
export const LightbulbIcon = Lightbulb;
export const Sparkles = m("Sparkle");
export const Rocket = m("Rocket");

// ───────── Security
export const Fingerprint = m("Fingerprint");
export const Key = m("Key");
export const Lock = m("Lock");
export const Power = m("Power");
export const Plug = m("Plug");
export const Shield = m("Shield");
export const ShieldAlert = m("ShieldWarning");
export const ShieldCheck = m("ShieldCheck");
export const Zap = m("Lightning");

// ───────── Globe / maps / connectivity
export const Globe = m("Globe");
export const Home = m("House");
export const MapPin = m("MapPin");
export const Wifi = m("WifiHigh");
export const WifiOff = m("WifiX");
export const Volume2 = m("SpeakerHigh");
export const VolumeX = m("SpeakerX");
export const Sun = m("Sun");
export const Moon = m("Moon");
export const Thermometer = m("Thermometer");
export const ThermometerSun = m("Thermometer");

// ───────── Misc UI
export const Hash = m("Hash");
export const Tag = m("Tag");
export const Tags = m("Tag");
export const Pin = m("PushPin");
export const Play = m("Play");
export const Pause = m("Pause");
export const Plus = m("Plus");
export const Add = Plus;
export const Minus = m("Minus");
export const X = m("X");
export const XCircle = m("XCircle");
export const XIcon = X;
export const ExternalLink = m("ArrowSquareOut");
export const Link = m("Link");
export const Link2 = m("LinkSimple");
export const LinkIcon = Link;
export const Share = m("ShareFat");
export const Share2 = m("ShareNetwork");
export const QrCode = m("QrCode");
export const Ticket = m("Ticket");
export const Palette = m("Palette");
export const Type = m("TextT");
export const Keyboard = m("Keyboard");
export const Monitor = m("Monitor");
export const MousePointer2 = m("CursorClick");
export const MousePointerClick = m("CursorClick");
export const Settings = m("Gear");
export const Settings2 = m("GearSix");
export const SettingsIcon = Settings;
export const LogOut = m("SignOut");

// ───────── Users
export const User = m("User");
export const UserCheck = m("UserCheck");
export const UserPlus = m("UserPlus");
export const UserX = m("UserMinus");
export const Users = m("Users");
export const Users2 = m("Users");
export const UsersRound = m("UsersThree");

// ───────── Brand logos (kept regular weight — duotone reads weird on logos)
const logo = (Icon: PhIcon): LucideIcon =>
  React.forwardRef<SVGSVGElement, any>(({ strokeWidth: _sw, weight, ...rest }, ref) =>
    React.createElement(Icon, { ref, weight: weight ?? "fill", ...rest })
  ) as unknown as LucideIcon;

const phLogo = (name: string): LucideIcon =>
  logo((PhAny[`${name}Icon`] ?? PhAny[name])!);

export const Twitter = phLogo("TwitterLogo");
export const Linkedin = phLogo("LinkedinLogo");
export const Instagram = phLogo("InstagramLogo");
export const Youtube = phLogo("YoutubeLogo");
export const Github = phLogo("GithubLogo");
export const Facebook = phLogo("FacebookLogo");
export const Reddit = phLogo("RedditLogo");
export const Telegram = phLogo("TelegramLogo");
export const Whatsapp = phLogo("WhatsappLogo");

// ───────── Additional mappings
export const BadgeCheck = m("SealCheck");
export const Banknote = m("Money");
export const Bot = m("Robot");
export const Camera = m("Camera");
export const ClipboardList = m("ClipboardText");
export const Factory = m("Factory");
export const FileCheck2 = m("FileCheck");
export const Globe2 = m("Globe");
export const KeyRound = m("Key");
export const Laptop = m("Laptop");
export const Plane = m("Airplane");
export const ShoppingBag = m("ShoppingBag");
export const Stethoscope = m("Stethoscope");

